'use client';

import { createClient } from '@/lib/supabase/client';


export interface WatchlistItem {
  id: string;
  user_id: string;
  film_id: string;
  added_at: string;
  films: {
    id: string;
    title: string;
    poster_url: string;
    poster_alt: string;
    runtime_display: string;
    genres: string[];
    year: number;
    film_type: string;
    festival_badge: string;
    content_rating: string;
    is_new: boolean;
    is_staff_pick: boolean;
  } | null;
}

export const watchlistService = {
  /** Fetch all watchlist film IDs for the current user */
  async getWatchlistIds(): Promise<string[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_lists')
      .select('film_id');
    if (error) return [];
    return (data || []).map((r: { film_id: string }) => r.film_id);
  },

  /** Fetch full watchlist with film details for the current user */
  async getWatchlist(): Promise<WatchlistItem[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_lists')
      .select(`
        id,
        user_id,
        film_id,
        added_at,
        films (
          id,
          title,
          poster_url,
          poster_alt,
          runtime_display,
          genres,
          year,
          film_type,
          festival_badge,
          content_rating,
          is_new,
          is_staff_pick
        )
      `)
      .order('added_at', { ascending: false });
    if (error) return [];
    return (data || []) as WatchlistItem[];
  },

  /** Add a film to the current user's watchlist */
  async addToWatchlist(filmId: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { error } = await supabase
      .from('user_lists')
      .insert({ user_id: user.id, film_id: filmId });
    return !error;
  },

  /** Remove a film from the current user's watchlist */
  async removeFromWatchlist(filmId: string): Promise<boolean> {
    const supabase = createClient();
    const { error } = await supabase
      .from('user_lists')
      .delete()
      .eq('film_id', filmId);
    return !error;
  },

  /** Toggle watchlist status for a film. Returns new state (true = in list). */
  async toggle(filmId: string, currentlyInList: boolean): Promise<boolean> {
    if (currentlyInList) {
      await watchlistService.removeFromWatchlist(filmId);
      return false;
    } else {
      const success = await watchlistService.addToWatchlist(filmId);
      return success;
    }
  },
};
